/**
 * Thin, injectable driver for the `portless` daemon (Layer B — see `.omc/plans/portless-layer-b.md`).
 *
 * `infra-kit dev` uses it to register `<release>.<package>.localhost → 127.0.0.1:<port>` routes so the
 * hero URLs resolve. Every call here is **time-bounded and never throws**: a missing binary, a non-zero
 * exit, or a wedged process resolves to `false`/no-op. That is a reporting contract, not a tolerance one —
 * portless IS a hard dependency of the dev loop, and `DevServerRunner.ensureProxy` turns a `false` from
 * this driver into a fatal, actionable start error.
 *
 * The binary is NOT resolved from `PATH`: `portless` is a normal npm dependency living in
 * `node_modules/.bin`, which is on `PATH` only when the process was launched via pnpm/npm. Since
 * `infra-kit dev` is often launched otherwise (a global bin, a cmux runner, a foreign cwd), we resolve
 * portless's own `dist/cli.js` by walking `node_modules` from this file (see {@link resolvePortlessBin})
 * and run it with the current `node` (`process.execPath`) — so it works regardless of how `dev` was
 * invoked. Args are fixed literals plus discovered release/package names + a numeric port, never
 * shell-interpolated.
 *
 * All process I/O is injected (`run` for awaited commands, `spawnDaemon` for the detached daemon) so
 * tests never shell out and assert the exact portless argv.
 */
import { execFile, spawn } from 'node:child_process'
import { existsSync, readFileSync } from 'node:fs'
import net from 'node:net'
import { homedir } from 'node:os'
import { dirname, join } from 'node:path'
import process from 'node:process'
import { fileURLToPath } from 'node:url'
import { promisify } from 'node:util'

import { PRIVILEGED_PORT_THRESHOLD } from 'src/lib/infra-kit-config'
import { withoutPackageManagerEnv } from 'src/lib/pm-env'

const execFileAsync = promisify(execFile)

/** Read `bin.portless` (the `dist/cli.js` relative path) from a portless `package.json` on disk. */
const readBinRel = (pkgJsonPath: string): string | null => {
  const pkg = JSON.parse(readFileSync(pkgJsonPath, 'utf-8')) as { bin?: string | Record<string, string> }
  const rel = typeof pkg.bin === 'string' ? pkg.bin : pkg.bin?.portless

  return rel == null || rel === '' ? null : rel
}

/**
 * Resolve the absolute path to portless's CLI entry (`portless/dist/cli.js`) from node_modules,
 * independent of `PATH`. Returns `null` when portless is not installed, degrading the whole driver to
 * a no-op.
 *
 * portless is ESM-only (its `.` export exposes only `import`/`types`, no `require`), so
 * `createRequire().resolve` can't see it. We instead walk `node_modules` upward from this file — the
 * standard resolution path — and read the package's `package.json` straight off disk, which bypasses the
 * exports map that would otherwise hide both `package.json` and the main entry.
 */
export const resolvePortlessBin = (): string | null => {
  try {
    let dir = dirname(fileURLToPath(import.meta.url))

    for (;;) {
      const pkgJsonPath = join(dir, 'node_modules', 'portless', 'package.json')

      if (existsSync(pkgJsonPath)) {
        const rel = readBinRel(pkgJsonPath)

        return rel == null ? null : join(dirname(pkgJsonPath), rel)
      }
      const parent = dirname(dir)

      if (parent === dir) return null
      dir = parent
    }
  } catch {
    return null
  }
}

/** Resolve portless's CLI once per process — the on-disk location never changes within a run. */
let cachedBin: string | null | undefined
const portlessBin = (): string | null => {
  if (cachedBin === undefined) cachedBin = resolvePortlessBin()

  return cachedBin
}

/** Awaited portless invocation. Rejects on non-zero exit / timeout; the driver swallows that into a no-op. */
export type PortlessRun = (args: string[], opts: { timeoutMs: number }) => Promise<void>

/** Fire-and-forget launch of the long-lived proxy daemon (detached; never awaited). */
export type SpawnDaemon = (args: string[]) => void

/**
 * Ground-truth liveness: is something accepting TCP on `127.0.0.1:<port>`? This is the ONLY reliable
 * "is the proxy up?" signal — `portless list`/`doctor` both exit 0 even with the daemon stopped, so a
 * subcommand exit code cannot gate daemon startup.
 */
export type IsListening = (port: number) => Promise<boolean>

const DEFAULT_TIMEOUT_MS = 1500
const READY_POLL_ATTEMPTS = 10
const READY_POLL_INTERVAL_MS = 200
const PROBE_TIMEOUT_MS = 500

export const defaultIsListening: IsListening = (port) => {
  return new Promise((resolve) => {
    const socket = net.connect({ host: '127.0.0.1', port })
    const finish = (result: boolean): void => {
      socket.destroy()
      resolve(result)
    }

    socket.setTimeout(PROBE_TIMEOUT_MS)
    socket.once('connect', () => {
      finish(true)
    })
    socket.once('timeout', () => {
      finish(false)
    })
    socket.once('error', () => {
      finish(false)
    })
  })
}

const defaultRun: PortlessRun = async (args, { timeoutMs }) => {
  const bin = portlessBin()

  if (bin == null) throw new Error('portless is not installed (not resolvable from node_modules)')
  await execFileAsync(process.execPath, [bin, ...args], {
    signal: AbortSignal.timeout(timeoutMs),
    encoding: 'utf-8',
    env: withoutPackageManagerEnv(process.env),
  })
}

const defaultSpawnDaemon: SpawnDaemon = (args) => {
  const bin = portlessBin()

  if (bin == null) return
  const child = spawn(process.execPath, [bin, ...args], {
    detached: true,
    stdio: 'ignore',
    env: withoutPackageManagerEnv(process.env),
  })

  child.unref()
}

const delay = (ms: number): Promise<void> => {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}

/**
 * portless's state directory. `resolveStateDir()` in portless ignores the bound port, so a daemon on
 * ANY port records itself here — which is what makes {@link defaultIsPortlessOwned} a valid identity check.
 */
const portlessStateDir = (): string => {
  return process.env.PORTLESS_STATE_DIR ?? join(homedir(), '.portless')
}

/**
 * Is the process listening on `port` actually portless, and not an unrelated squatter?
 *
 * A TCP probe alone cannot answer this: `isListening` only proves *something* accepts connections. If
 * that something is not portless, we would register aliases into a route table nobody serves and print
 * hero URLs that never resolve. So cross-check portless's own state files — `proxy.port` must name this
 * port AND `proxy.pid` must still be alive. Both are stale-prone on their own (a killed daemon leaves
 * both behind), which is exactly why neither is trusted alone.
 */
export type IsPortlessOwned = (port: number) => boolean

export const defaultIsPortlessOwned: IsPortlessOwned = (port) => {
  try {
    const dir = portlessStateDir()

    if (Number(readFileSync(join(dir, 'proxy.port'), 'utf-8').trim()) !== port) return false

    const pid = Number(readFileSync(join(dir, 'proxy.pid'), 'utf-8').trim())

    if (!Number.isInteger(pid) || pid <= 0) return false

    // Signal 0 checks liveness without delivering anything. EPERM means the pid exists but belongs to
    // another user — still alive, so still portless's daemon.
    try {
      process.kill(pid, 0)
    } catch (error) {
      return (error as NodeJS.ErrnoException).code === 'EPERM'
    }

    return true
  } catch {
    return false
  }
}

export interface PortlessDriver {
  /** Resolve (and memoize) whether the `portless` binary is usable. Absent → every other call no-ops. */
  isAvailable: () => Promise<boolean>
  /**
   * Ensure the proxy daemon is listening on `port` (probe first; start detached + bounded readiness
   * poll). Returns `true` only when the daemon is confirmed listening — the caller gates hero URLs on it.
   *
   * A privileged port (< 1024, the default `80`) is PROBED ONLY, never started: binding it needs root,
   * and portless's sudo re-exec cannot prompt from a detached child. Bring it up once out-of-band with
   * `sudo portless service install --no-tls`.
   */
  ensureDaemon: (port: number) => Promise<boolean>
  /**
   * Register `<name> → 127.0.0.1:<port>` (`name` = `<release>.<package>`). Returns `true` on success so
   * the caller shows the hero URL only for an alias that actually resolves (best-effort otherwise).
   */
  registerAlias: (name: string, port: number) => Promise<boolean>
  /** Deregister `<name>`. Best-effort. */
  removeAlias: (name: string) => Promise<void>
}

export interface PortlessDriverDeps {
  run?: PortlessRun
  spawnDaemon?: SpawnDaemon
  /** TCP liveness probe (default: real `net` connect). Injected in tests to simulate a daemon coming up. */
  isListening?: IsListening
  /** Identity check: is the listener on this port portless itself? Injected in tests. */
  isPortlessOwned?: IsPortlessOwned
  timeoutMs?: number
}

/**
 * Build a {@link PortlessDriver}. Inject `run`/`spawnDaemon` in tests to assert argv without shelling
 * out. `isAvailable` memoizes so the binary is probed at most once per runner.
 */
export const createPortlessDriver = (deps: PortlessDriverDeps = {}): PortlessDriver => {
  const run = deps.run ?? defaultRun
  const spawnDaemon = deps.spawnDaemon ?? defaultSpawnDaemon
  const isListening = deps.isListening ?? defaultIsListening
  const isPortlessOwned = deps.isPortlessOwned ?? defaultIsPortlessOwned
  const timeoutMs = deps.timeoutMs ?? DEFAULT_TIMEOUT_MS
  let availability: boolean | null = null

  /** Run a portless subcommand, swallowing any failure into `false` (best-effort contract). */
  const tryRun = async (args: string[]): Promise<boolean> => {
    try {
      await run(args, { timeoutMs })

      return true
    } catch {
      return false
    }
  }

  const isAvailable = async (): Promise<boolean> => {
    if (availability === null) {
      availability = await tryRun(['--version'])
    }

    return availability
  }

  const ensureDaemon = async (port: number): Promise<boolean> => {
    if (!(await isAvailable())) return false
    // Already accepting TCP on the proxy port? Nothing to start. (Ground truth — NOT `portless list`,
    // which exits 0 even when the daemon is stopped and would falsely skip the start below.) A listener
    // we cannot identify as portless is a squatter, not our daemon: claiming it would publish hero URLs
    // that route nowhere, so report it as unusable and let the caller fail loudly.
    if (await isListening(port)) return isPortlessOwned(port)

    // A privileged port can only be BOUND by root. portless handles that by re-execing itself through
    // `sudo` with `stdio:'inherit'` — which a detached `stdio:'ignore'` child can never satisfy: the
    // password prompt has nowhere to go, so the start would hang until the timeout and then report a
    // daemon that never came up. Probe only; the caller tells the user how to install the daemon once.
    if (port < PRIVILEGED_PORT_THRESHOLD) return false

    // Start detached (`--no-tls -p <port>` → plain HTTP, zero-sudo) so a foreground daemon can't block
    // us, then poll TCP readiness within a bounded window.
    spawnDaemon(['proxy', 'start', '--no-tls', '-p', String(port)])
    for (let attempt = 0; attempt < READY_POLL_ATTEMPTS; attempt += 1) {
      await delay(READY_POLL_INTERVAL_MS)
      // BOTH conditions, and keep polling until both hold: the daemon can accept TCP a beat before it
      // writes `proxy.port`/`proxy.pid`, so bailing on the identity check alone would abort a start that
      // was merely a few milliseconds from ready.
      if ((await isListening(port)) && isPortlessOwned(port)) return true
    }

    return false
  }

  const registerAlias = async (name: string, port: number): Promise<boolean> => {
    if (!(await isAvailable())) return false

    return tryRun(['alias', name, String(port)])
  }

  const removeAlias = async (name: string): Promise<void> => {
    if (!(await isAvailable())) return
    await tryRun(['alias', '--remove', name])
  }

  return { isAvailable, ensureDaemon, registerAlias, removeAlias }
}
